<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'dashboard/components-next/polaris/ui/tooltip';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const agentActions = agent =>
  [showEditAction(agent) && 'edit', showDeleteAction(agent) && 'delete'].filter(
    Boolean
  );

const tableHeaders = computed(() => [
  t('AGENT_MGMT.LIST.NAME'),
  t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL'),
  t('AGENT_MGMT.LIST.STATUS'),
  t('AGENT_MGMT.LIST.ACTIONS'),
]);

const showAlertMessage = message => {
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const onRowAction = (action, agent) => {
  if (action === 'edit') openEditPopup(agent);
  else openDeletePopup(agent);
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
        </template>
        <template #actions>
          <Button
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #emptyActions>
      <Button
        :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
        size="sm"
        outline
        @click="openAddPopup"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredAgentList"
        :no-data-message="$t('AGENT_MGMT.NO_RESULTS')"
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="agent in items" :key="agent.email" :item="agent">
            <BaseTableCell>
              <div class="flex min-w-0 items-center gap-3">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.name"
                  :status="agent.availability_status"
                  :size="32"
                  hide-offline-status
                />
                <div class="grid min-w-0">
                  <span class="truncate">{{ agent.name }}</span>
                  <span
                    class="truncate type-polaris-caption font-normal text-polaris-muted-foreground"
                  >
                    {{ agent.email }}
                  </span>
                </div>
              </div>
            </BaseTableCell>
            <BaseTableCell>
              <Tooltip v-if="agent.custom_role_id">
                <TooltipTrigger as-child>
                  <span
                    tabindex="0"
                    class="cursor-default underline decoration-dotted underline-offset-4"
                  >
                    {{ getAgentRoleName(agent) }}
                  </span>
                </TooltipTrigger>
                <TooltipContent class="max-w-72">
                  <p class="font-medium">
                    {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                  </p>
                  <ul class="m-0 list-disc ps-4">
                    <li
                      v-for="permission in getAgentRolePermissions(agent)"
                      :key="permission"
                    >
                      {{
                        $t(
                          `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                        )
                      }}
                    </li>
                  </ul>
                </TooltipContent>
              </Tooltip>
              <template v-else>{{ getAgentRoleName(agent) }}</template>
            </BaseTableCell>
            <BaseTableCell>
              {{
                agent.confirmed
                  ? $t('AGENT_MGMT.LIST.VERIFIED')
                  : $t('AGENT_MGMT.LIST.VERIFICATION_PENDING')
              }}
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                v-if="agentActions(agent).length"
                :label="rowMenuLabel(agent.name)"
                :items="rowMenuItems(agentActions(agent))"
                @action="action => onRowAction(action, agent)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :availability="currentAgent.availability_status"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </SettingsLayout>
</template>
